import { Icon, Logo, Popover, cn } from '@labflow/ui';
import { useRouterState } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { type MouseEvent, type ReactNode, useState } from 'react';

import { NAV_ITEMS } from '../../lib/navigation';
import { ShellLink } from './shell-link';

type ModuleItem = (typeof NAV_ITEMS)[number];

export interface AppHeaderProps {
  /** 지금 모듈 — 계정 영역·랜딩이면 없다 */
  activeModule?: ModuleItem;
  /** 모듈 클릭을 가로챈다(온보딩 미완료 — `AC-PF-01-12`). true를 돌려주면 이동하지 않는다. */
  onModuleClick?: (item: ModuleItem) => boolean;
  /** 모듈 4개를 그린다 — 로그인하지 않았으면 닿을 수 없는 곳이라 로고와 로그인만 둔다 (D154 ①). */
  showModules?: boolean;
  user: ReactNode;
}

/**
 * 모듈 링크의 현재 위치. 모듈 첫 화면 그 자체이고 서브내비가 없을 때만 `page`다 —
 * My Lab은 서브내비 `오늘`이 현재 페이지를 맡는다 (D145 ⑤).
 */
function moduleCurrent(item: ModuleItem, pathname: string): 'page' | 'true' | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path !== item.to && !path.startsWith(`${item.to}/`)) return undefined;
  return path === item.to && item.to !== '/mylab' ? 'page' : 'true';
}

const moduleLink = 'hover:text-ink ease-flow duration-fast text-sm font-medium transition-colors';

/** 헤더 — 로고 · 모듈 4개 · 사용자 영역 (S0 §8.6). 현재 위치 바는 서브내비만 갖는다. */
export function AppHeader({
  activeModule,
  onModuleClick,
  showModules = true,
  user,
}: AppHeaderProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  function handleClick(item: ModuleItem) {
    return (event: MouseEvent) => {
      if (onModuleClick?.(item)) event.preventDefault();
    };
  }

  return (
    <header className="border-line bg-canvas px-gutter gap-card flex h-16 shrink-0 items-center justify-between border-b">
      <nav className="gap-panel flex min-w-0 items-center md:gap-11">
        <ShellLink to="/">
          <Logo size="lg" />
        </ShellLink>

        {showModules ? (
          <ul aria-label="주 메뉴" className="gap-panel hidden items-center md:flex">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <ShellLink
                  to={item.to}
                  className={moduleLink}
                  activeClassName="text-action"
                  inactiveClassName="text-ink-muted"
                  current={moduleCurrent(item, pathname)}
                  onClick={handleClick(item)}
                >
                  {item.label}
                </ShellLink>
              </li>
            ))}
          </ul>
        ) : null}

        {showModules ? (
          <ModuleSwitcher activeModule={activeModule} onItemClick={handleClick} />
        ) : null}
      </nav>

      <div className="gap-card flex shrink-0 items-center">{user}</div>
    </header>
  );
}

/** 768px 미만에서만 모듈 4개를 현재 모듈 이름 메뉴로 접는다 (D145 ⑧). 이동 목록이라 링크다. */
function ModuleSwitcher({
  activeModule,
  onItemClick,
}: {
  activeModule?: ModuleItem;
  onItemClick: (item: ModuleItem) => (event: MouseEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return (
    <div className="md:hidden">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger
          className={cn(
            'gap-tight text-ink flex items-center rounded-md text-sm font-medium',
            'focus-visible:ring-focus outline-none focus-visible:ring-2',
          )}
        >
          {activeModule?.label ?? '메뉴'}
          <Icon as={ChevronDown} size="sm" />
        </Popover.Trigger>
        <Popover.Content className="p-tight w-48">
          <ul aria-label="주 메뉴" className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <ShellLink
                  to={item.to}
                  className="hover:bg-highlight px-snug flex h-9 items-center rounded-md text-sm font-medium"
                  activeClassName="text-action"
                  inactiveClassName="text-ink-muted"
                  current={moduleCurrent(item, pathname)}
                  onClick={(event) => {
                    onItemClick(item)(event);
                    setOpen(false);
                  }}
                >
                  {item.label}
                </ShellLink>
              </li>
            ))}
          </ul>
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}
